/**
 * Centralized event service.
 *
 * Every meaningful user action (create, update, delete, navigate, authenticate,
 * export, invite) emits a named event through this service.
 *
 * Naming convention: category.action
 *   ✅ project.created  ✅ user.invited  ✅ report.exported
 *   ❌ button.clicked   ❌ tab.switched  (low-value UI interactions — don't track)
 *
 * Adding an analytics provider (PostHog, GA4, Segment) means adding an adapter
 * in the adapters array below — never touching application code.
 *
 * @spec SPEC:ENGINEERING:FRONTEND:ANALYTICS
 *
 * Usage:
 *   import { events } from '@/services/events'
 *   events.track('project.created', { projectId: '123', name: 'My App' })
 */
import posthog from 'posthog-js'
import { isDev, env } from '@/env'
import { logger } from '@/services/logger'

type EventProperties = Record<string, string | number | boolean | null | undefined>

interface EventAdapter {
  name: string
  track: (event: string, properties?: EventProperties) => void
  identify?: (userId: string, traits?: EventProperties) => void
  reset?: () => void
  captureException?: (error: Error, context?: EventProperties) => void
  register?: (properties: EventProperties) => void
}

// ── PostHog initialization ────────────────────────────────────────────────────
// Initialize once at module load (before any adapter is constructed) so PostHog
// is ready even for events fired immediately on app mount. The npm package is
// bundled synchronously — no script-tag race condition.
if (!isDev && env.VITE_POSTHOG_KEY) {
  posthog.init(env.VITE_POSTHOG_KEY, {
    api_host: env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com',
    // We fire pageviews manually from the router on each navigation
    capture_pageview: false,
    capture_pageleave: true,
    // Autocapture on — retroactive analysis beats manual coverage at this stage
    autocapture: true,
    // Catch unhandled errors + promise rejections for PostHog Error Tracking
    capture_exceptions: true,
    // Session recording on by default, input masking off (no PII in this app)
    session_recording: { maskAllInputs: false, maskTextSelector: '' },
  })
}

// ── Adapters ─────────────────────────────────────────────────────────────────
// Add production adapters here. Each adapter is isolated — a broken adapter
// must never crash the application (errors are caught and logged).

const consoleAdapter: EventAdapter = {
  name: 'console',
  track: (event, properties) => {
    logger.debug(`[events] ${event}`, properties)
  },
  identify: (userId, traits) => {
    logger.debug('[events] identify', { userId, ...traits })
  },
  reset: () => {
    logger.debug('[events] reset')
  },
  captureException: (error, context) => {
    logger.error('[events] captureException', error, context)
  },
  register: (properties) => {
    logger.debug('[events] register', properties)
  },
}

const posthogAdapter: EventAdapter = {
  name: 'posthog',
  track: (event, properties) => {
    posthog.capture(event, properties)
  },
  identify: (userId, traits) => {
    posthog.identify(userId, traits)
  },
  reset: () => {
    posthog.reset()
  },
  captureException: (error, context) => {
    posthog.captureException(error, { extra: context })
  },
  register: (properties) => {
    posthog.register(properties)
  },
}

function getAdapters(): EventAdapter[] {
  // Local dev: console only — PostHog is opted out in isDev check above
  if (isDev) return [consoleAdapter]
  // Staging + production: PostHog + console (console stays for debugging)
  return [posthogAdapter, consoleAdapter]
}

function safeCall(adapter: EventAdapter, fn: () => void) {
  try {
    fn()
  } catch (err) {
    logger.error(`[events] adapter "${adapter.name}" threw`, err)
  }
}

const adapters = getAdapters()

export const events = {
  /**
   * Track a user action.
   * @param event - Dot-separated category.action name (e.g. 'project.created')
   * @param properties - Optional key/value metadata (IDs, counts, labels)
   */
  track(event: string, properties?: EventProperties) {
    for (const adapter of adapters) {
      safeCall(adapter, () => adapter.track(event, properties))
    }
  },

  /**
   * Identify the current user. Call after successful login.
   * @param userId - Stable user identifier
   * @param traits - User properties (email, name, role, plan)
   */
  identify(userId: string, traits?: EventProperties) {
    for (const adapter of adapters) {
      if (adapter.identify) {
        safeCall(adapter, () => adapter.identify!(userId, traits))
      }
    }
  },

  /**
   * Reset identity. Call on logout to prevent cross-user data leakage.
   */
  reset() {
    for (const adapter of adapters) {
      if (adapter.reset) {
        safeCall(adapter, () => adapter.reset!())
      }
    }
  },

  /**
   * Capture an unhandled exception. Called automatically by ErrorBoundary.
   * PostHog links exceptions to the current session recording automatically.
   * @param error - The caught Error object
   * @param context - Optional extra metadata (e.g. { componentStack })
   */
  captureException(error: Error, context?: EventProperties) {
    for (const adapter of adapters) {
      if (adapter.captureException) {
        safeCall(adapter, () => adapter.captureException!(error, context))
      }
    }
  },

  /**
   * Register super properties — included automatically in all subsequent events.
   * Call after login with { orgId } so every event carries org context.
   * Cleared on reset() (logout).
   * @param properties - Key/value pairs to attach to all future events
   */
  register(properties: EventProperties) {
    for (const adapter of adapters) {
      if (adapter.register) {
        safeCall(adapter, () => adapter.register!(properties))
      }
    }
  },
}
